import * as THREE from 'three';
import { rotateCornersFromStart } from './cornerMarkers.js';

const _tangent = new THREE.Vector3();
const _side = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _lookAt = new THREE.Vector3();
const _camPos = new THREE.Vector3();

/** Slower ease-in from the default overview camera to the first start-line dwell. */
const INITIAL_APPROACH_SNAP = 1.15;
/** Loose thresholds — damped orbit controls rarely settle within a tight epsilon. */
const INITIAL_APPROACH_POSITION_EPS = 0.55;
const INITIAL_APPROACH_TARGET_EPS = 0.45;
/** Never block the lap sequence if the camera never fully converges. */
const INITIAL_APPROACH_MAX_SECONDS = 10;
/** Brief hold at the start line after the overview fly-in; corners use getDwellSeconds(). */
const START_LINE_DWELL_SECONDS = 1.25;

/**
 * Shortest-path interpolation on a closed curve (t ∈ [0, 1]).
 */
export function lerpCurveT(t0, t1, u) {
  let delta = t1 - t0;
  if (delta > 0.5) delta -= 1;
  if (delta < -0.5) delta += 1;
  let t = t0 + delta * u;
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  return t;
}

/** Forward (increasing t) interpolation on a closed curve. */
export function lerpCurveTForward(t0, t1, u) {
  let delta = t1 - t0;
  if (delta <= 0) delta += 1;
  let t = t0 + delta * u;
  if (t > 1) t -= 1;
  return t;
}

/** Reverse (decreasing t) interpolation on a closed curve. */
export function lerpCurveTReverse(t0, t1, u) {
  let delta = t0 - t1;
  if (delta <= 0) delta += 1;
  let t = t0 - delta * u;
  if (t < 0) t += 1;
  return t;
}

function forwardDeltaT(t0, t1) {
  let delta = t1 - t0;
  if (delta <= 0) delta += 1;
  return delta;
}

/**
 * Arc length along a closed curve between two normalized t values.
 * @param {THREE.Curve} curve
 * @param {number} t0
 * @param {number} t1
 * @param {boolean} clockwise
 */
export function segmentArcLength(curve, t0, t1, clockwise) {
  const delta = clockwise ? forwardDeltaT(t0, t1) : forwardDeltaT(t1, t0);
  return delta * curve.getLength();
}

/**
 * Travel duration for one lap leg from distance and speed, clamped to min/max.
 */
export function travelSecondsForLeg(
  curve,
  t0,
  t1,
  clockwise,
  speed,
  minSeconds,
  maxSeconds,
  timeMultiplier = 1,
) {
  const length = segmentArcLength(curve, t0, t1, clockwise);
  const safeSpeed = Math.max(0.1, speed);
  const safeMultiplier = Math.max(0.1, timeMultiplier);
  const raw = (length / safeSpeed) * safeMultiplier;
  const lo = Math.min(minSeconds, maxSeconds);
  const hi = Math.max(minSeconds, maxSeconds);
  return Math.max(lo, Math.min(hi, raw));
}

function smoothstep(u) {
  const x = Math.max(0, Math.min(1, u));
  return x * x * (3 - 2 * x);
}

/**
 * Broadcast-style camera pose looking along the circuit.
 * @param {'dwell' | 'travel'} mode
 */
export function sampleTourCameraPose(curve, t, liftY, mode = 'travel') {
  const point = curve.getPointAt(t);
  curve.getTangentAt(t, _tangent);
  _tangent.y = 0;

  if (_tangent.lengthSq() < 1e-8) {
    _tangent.set(0, 0, 1);
  } else {
    _tangent.normalize();
  }

  _side.crossVectors(_up, _tangent).normalize();

  const dwell = mode === 'dwell';
  const height = dwell ? 1.85 : 3.6;
  const sideOffset = dwell ? 0.45 : 1.25;
  const behind = dwell ? -0.2 : -1.05;
  const lookAhead = dwell ? 0.08 : 0.55;

  _lookAt.copy(point);
  _lookAt.y = liftY;
  _lookAt.addScaledVector(_tangent, lookAhead);

  _camPos.copy(point);
  _camPos.y = liftY;
  _camPos.addScaledVector(_side, sideOffset);
  _camPos.addScaledVector(_tangent, behind);
  _camPos.y += height;

  return {
    target: _lookAt.clone(),
    position: _camPos.clone(),
  };
}

function toWorldPose(pose, worldRoot) {
  if (!worldRoot) return pose;
  return {
    target: worldRoot.localToWorld(pose.target),
    position: worldRoot.localToWorld(pose.position),
  };
}

/**
 * Lap-style tour: dwell at each corner, then travel along the circuit path.
 */
export function createLapTour({
  getCurve,
  getMarkers,
  getStartMarker = () => null,
  getStartT = () => null,
  getLapClockwise = () => true,
  minCornerSpacing = 0.025,
  getLiftY,
  getWorldRoot,
  getDwellSeconds,
  getTravelSpeed,
  getTravelMin,
  getTravelMax,
  getTravelMultiplier = () => 1,
  onShowCorner,
  onHideCorner,
  onTravelProgress,
}) {
  let running = false;
  /** @type {{ marker: THREE.Object3D; t: number }[]} */
  let corners = [];
  let cornerIndex = 0;
  let phase = 'dwell';
  let elapsed = 0;
  let userCameraControl = false;
  let initialApproachActive = false;
  let approachElapsed = 0;
  let currentTravelSeconds = 2.8;

  /** @type {{ target: THREE.Vector3; position: THREE.Vector3 } | null} */
  let goalPose = null;

  function sortedCorners() {
    const cornerEntries = [...getMarkers()].map((marker) => ({
      marker,
      t: marker.userData.cornerT ?? 0,
    }));

    const startMarker = getStartMarker();
    const startT = getStartT();
    const pivotT =
      startMarker && startT != null && Number.isFinite(startT)
        ? startT
        : startT != null && Number.isFinite(startT)
          ? startT
          : 0;

    const filtered = cornerEntries.filter((c) => {
      if (startT == null || !Number.isFinite(startT)) return true;
      return Math.abs(c.t - startT) >= minCornerSpacing;
    });

    const clockwise = getLapClockwise();
    let rotated = rotateCornersFromStart(filtered, pivotT, clockwise);

    if (startMarker && startT != null && Number.isFinite(startT)) {
      const stops = [{ marker: startMarker, t: startT }, ...rotated];
      if (!clockwise && stops.length > 1) {
        return [stops[0], ...stops.slice(1).reverse()];
      }
      return stops;
    }

    if (!clockwise) rotated = [...rotated].reverse();
    return rotated;
  }

  function setGoalFromT(t, mode) {
    const curve = getCurve();
    if (!curve) return;
    const local = sampleTourCameraPose(curve, t, getLiftY(), mode);
    goalPose = toWorldPose(local, getWorldRoot());
  }

  function beginDwell(i) {
    cornerIndex = i;
    phase = 'dwell';
    elapsed = 0;
    setGoalFromT(corners[i].t, 'dwell');
    onShowCorner?.(corners[i].marker, i, corners.length);
  }

  function beginTravel() {
    phase = 'travel';
    elapsed = 0;
    onHideCorner?.();
    const n = corners.length;
    const clockwise = getLapClockwise();
    const toIndex = clockwise
      ? (cornerIndex + 1) % n
      : (cornerIndex - 1 + n) % n;

    const curve = getCurve();
    if (curve) {
      currentTravelSeconds = travelSecondsForLeg(
        curve,
        corners[cornerIndex].t,
        corners[toIndex].t,
        clockwise,
        getTravelSpeed(),
        getTravelMin(),
        getTravelMax(),
        getTravelMultiplier(),
      );
    } else {
      currentTravelSeconds = getTravelMax();
    }

    onTravelProgress?.(cornerIndex, toIndex, corners.length);
  }

  function start() {
    corners = sortedCorners();
    if (corners.length === 0 || !getCurve()) return false;
    running = true;
    initialApproachActive = true;
    approachElapsed = 0;
    beginDwell(0);
    return true;
  }

  function stop() {
    running = false;
    cornerIndex = 0;
    phase = 'dwell';
    elapsed = 0;
    goalPose = null;
    userCameraControl = false;
    initialApproachActive = false;
    approachElapsed = 0;
  }

  function finishInitialApproach() {
    initialApproachActive = false;
    approachElapsed = 0;
  }

  function dwellSecondsForIndex(index) {
    const marker = corners[index]?.marker;
    if (marker?.userData.isStartMarker) {
      return START_LINE_DWELL_SECONDS;
    }
    return getDwellSeconds();
  }

  function tick(delta, camera, controls) {
    if (!running || !goalPose) return;

    const curve = getCurve();
    if (!curve) {
      stop();
      return;
    }

    const travel = currentTravelSeconds;

    if (phase === 'dwell') {
      setGoalFromT(corners[cornerIndex].t, 'dwell');
      if (!initialApproachActive) {
        elapsed += delta;
      }

      if (elapsed >= dwellSecondsForIndex(cornerIndex)) {
        beginTravel();
      }
    } else {
      elapsed += delta;
      const u = smoothstep(elapsed / Math.max(0.1, travel));
      const n = corners.length;
      const clockwise = getLapClockwise();
      const toIndex = clockwise
        ? (cornerIndex + 1) % n
        : (cornerIndex - 1 + n) % n;
      const lerpT = clockwise ? lerpCurveTForward : lerpCurveTReverse;
      let t;
      if (corners.length === 1) {
        t = clockwise
          ? lerpCurveTForward(corners[0].t, corners[0].t + 0.001, u)
          : lerpCurveTReverse(corners[0].t, corners[0].t - 0.001, u);
      } else {
        t = lerpT(corners[cornerIndex].t, corners[toIndex].t, u);
      }
      const local = sampleTourCameraPose(curve, t, getLiftY(), 'travel');
      goalPose = toWorldPose(local, getWorldRoot());

      if (u >= 1) {
        beginDwell(toIndex);
      }
    }

    let snap = phase === 'dwell' ? 10 : 6;
    if (initialApproachActive && phase === 'dwell') {
      snap = INITIAL_APPROACH_SNAP;
    }

    const blend = 1 - Math.exp(-snap * delta);
    controls.target.lerp(goalPose.target, blend);

    if (!userCameraControl) {
      camera.position.lerp(goalPose.position, blend);
    }

    if (initialApproachActive && phase === 'dwell') {
      approachElapsed += delta;
      const posDist = camera.position.distanceTo(goalPose.position);
      const targetDist = controls.target.distanceTo(goalPose.target);
      const arrived =
        posDist < INITIAL_APPROACH_POSITION_EPS &&
        targetDist < INITIAL_APPROACH_TARGET_EPS;
      if (arrived || approachElapsed >= INITIAL_APPROACH_MAX_SECONDS) {
        finishInitialApproach();
      }
    }
  }

  function setUserCameraControl(enabled) {
    userCameraControl = enabled;
  }

  function resetUserCameraControl() {
    userCameraControl = false;
  }

  return {
    start,
    stop,
    tick,
    setUserCameraControl,
    resetUserCameraControl,
    isUserCameraControl: () => userCameraControl,
    isRunning: () => running,
    getIndex: () => cornerIndex,
    getPhase: () => phase,
    getSortedMarkers: () => corners.map((c) => c.marker),
    getActiveMarker: () => corners[cornerIndex]?.marker ?? null,
  };
}
